<template>
  <div>
    <!-- ヘッダー -->
    <v-row>
      <v-col cols="12">
        <h2>
          <v-icon class="mr-2"> mdi-list-status </v-icon>
          マニフェスト登録件数
        </h2>
        <v-spacer class="mt-2" />
      </v-col>
    </v-row>
    <!-- /ヘッダー -->

    <!-- 検索ブロック -->
    <v-row>
      <v-col>
        <v-expansion-panels accordion>
          <v-expansion-panel>
            <v-expansion-panel-header
              class="panel-header-color panel-selectable"
            >
              <strong>
                <v-icon class="panel-header-color"> mdi-magnify </v-icon>
                &nbsp;条件を指定して検索
              </strong>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-card flat>
                <v-card-text>
                  <v-row>
                    <v-col cols="12" md="2">
                      <cs-date-picker
                        :date.sync="searchForms.startMonth"
                        type="month"
                        @dateChangedCallback="startMonthChangedCallback"
                      >
                        <template v-slot="{ on, attrs }">
                          <v-text-field
                            v-model="searchForms.startMonth"
                            v-bind="attrs"
                            v-on="on"
                            :error-messages="errors"
                            append-icon="mdi-calendar"
                            dense
                            clearable
                            hide-details="auto"
                            label="対象年月(From)"
                            outlined
                            rounded
                            persistent-hint
                            persistent-placeholder
                            readonly
                            @click:clear="searchForms.startMonth = null; searchButtonClickEvent()"
                          >
                            <template v-slot:append-outer>
                              <div
                                v-if="$vuetify.breakpoint.mdAndUp"
                                style="vertical-align: middle"
                              >
                                〜
                              </div>
                            </template>
                          </v-text-field>
                        </template>
                      </cs-date-picker>
                    </v-col>

                    <v-col cols="12" md="2">
                      <cs-date-picker
                        :date.sync="searchForms.endMonth"
                        type="month"
                        @dateChangedCallback="endMonthChangedCallback"
                      >
                        <template v-slot="{ on, attrs }">
                          <v-text-field
                            v-model="searchForms.endMonth"
                            v-bind="attrs"
                            v-on="on"
                            :error-messages="errors"
                            append-icon="mdi-calendar"
                            dense
                            clearable
                            hide-details="auto"
                            label="対象年月(To)"
                            outlined
                            rounded
                            persistent-hint
                            persistent-placeholder
                            readonly
                            @click:clear="searchForms.endMonth = null; searchButtonClickEvent()"
                          />
                        </template>
                      </cs-date-picker>
                    </v-col>
                  </v-row>
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn color="primary" @click="searchButtonClickEvent">
                    <v-icon> mdi-magnify </v-icon>
                    &nbsp;検索
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
    </v-row>
    <!-- /検索ブロック -->

    <!-- ボタン -->
    <v-row>
      <v-col cols="12" md="2" class="text-left">
        <h4>{{ total_count }} 件</h4>
      </v-col>
      <v-col cols="12" md="10" class="text-right">
        <v-btn
          color="info"
          @click="exportButtonClickEvent"
        >
          <v-icon>mdi-file-download-outline</v-icon>&nbsp;CSV出力
        </v-btn>
      </v-col>
    </v-row>
    <!-- /ボタン -->

    <v-row>
      <v-col>
        <v-card class="rounded-card">
          <v-card-text>
            <v-data-table
              :headers="tableHeaders"
              :items="manifest_reports"
              :items-per-page="-1"
              disable-pagination
              disable-sort
              hide-default-footer
            >
              <!-- 年月 -->
              <template v-slot:[`item.target_date`]="{ item }">
                <span class="font-weight-black">
                  {{ item.target_date | date_format('YYYY/MM') }}
                </span>
              </template>
              <!-- /年月 -->
            
              <!-- A料金 -->
              <template v-slot:[`item.plan_a_count`]="{ item }">
                <span class="font-weight-black">
                  {{ parseFloat(item.plan_a_count) | comma_format }}
                </span>
              </template>
              <!-- /A料金 -->

              <!-- B料金 -->
              <template v-slot:[`item.plan_b_count`]="{ item }">
                <span class="font-weight-black">
                  {{ parseFloat(item.plan_b_count) | comma_format }}
                </span>
              </template>
              <!-- /B料金 -->

              <!-- C料金 -->
              <template v-slot:[`item.plan_c_count`]="{ item }">
                <span class="font-weight-black">
                  {{ parseFloat(item.plan_c_count) | comma_format }}
                </span>
              </template>
              <!-- /C料金 -->

              <!-- C料金（DXE） -->
              <template v-slot:[`item.plan_c_dxe_count`]="{ item }">
                <span class="font-weight-black">
                  {{ parseFloat(item.plan_c_dxe_count) | comma_format }}
                </span>
              </template>
              <!-- /C料金（DXE） -->

              <!-- 不明料金 -->
              <template v-slot:[`item.plan_none_count`]="{ item }">
                <span class="font-weight-black">
                  {{ parseFloat(item.plan_none_count) | comma_format }}
                </span>
              </template>
              <!-- /不明料金 -->

              <!-- 解約(C料金(DXE)) -->
              <template v-slot:[`item.plan_canceled_c_dxe_count`]="{ item }">
                <span class="font-weight-black">
                  {{ parseFloat(item.plan_canceled_c_dxe_count) | comma_format }}
                </span>
              </template>
              <!-- /解約(C料金(DXE)) -->

              <!-- Webマニフェスト -->
              <template v-slot:[`item.type_web_count`]="{ item }">
                <span class="font-weight-black">
                  {{ parseFloat(item.type_web_count) | comma_format }}
                </span>
              </template>
              <!-- /Webマニフェスト -->

              <!-- 紙マニフェスト -->
              <template v-slot:[`item.type_paper_count`]="{ item }">
                <span class="font-weight-black">
                  {{ parseFloat(item.type_paper_count) | comma_format }}
                </span>
              </template>
              <!-- /紙マニフェスト -->

              <!-- 合計数 -->
              <template v-slot:[`item.total_count`]="{ item }">
                <span class="font-weight-black">
                  {{ parseFloat(item.total_count) | comma_format }}
                </span>
              </template>
              <!-- /合計数 -->

              <!-- データ送信数 -->
              <template v-slot:[`item.send_count`]="{ item }">
                <span class="font-weight-black">
                  {{ parseFloat(item.send_count) | comma_format }}
                </span>
              </template>
              <!-- /データ送信数 -->

              <!-- データ送信数 -->
              <template v-slot:[`item.jwnet_transfer_count`]="{ item }">
                <span class="font-weight-black">
                  {{ parseFloat(item.jwnet_transfer_count) | comma_format }}
                </span>
              </template>
              <!-- /データ送信数 -->

            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- ページャ -->
    <v-row v-if="hasResult" dense>
      <v-col>
        <v-pagination
          v-model="page"
          :length="pagination.total_pages"
          :total-visible="totalVisible"
          circle
          prev-icon="mdi-menu-left"
          next-icon="mdi-menu-right"
          @input="changePage"
        />
      </v-col>
    </v-row>
    <!-- /ページャ -->
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import DownloadMixin from "../../../mixins/download-mixin";
import moment from "moment";

export default {
  name: "ManifestReportIndex",
  components: {},
  mixins: [DownloadMixin],
  data() {
    return {
      page: 1,
      totalVisible: 7,
      showTargetDatePicker: false,
      total_count: null,
      searchForms: {
        startMonth: moment().format("YYYY-MM"),
        endMonth: moment().format("YYYY-MM"),
      },
      tableHeaders: [
        { value: "tenant_name", text: "テナント名" },
        { value: "contract_unit_type_name", text: "契約単位種別" },
        { value: "price_plan_name", text: "料金プラン" },
        { value: "target_date", text: "年月" },
        { value: "plan_a_count", text: "A料金", align: "right" },
        { value: "plan_b_count", text: "B料金", align: "right" },
        { value: "plan_c_count", text: "C料金", align: "right" },
        { value: "plan_c_dxe_count", text: "C料金(DXE)", align: "right" },
        { value: "plan_none_count", text: "不明料金", align: "right" },
        { value: "plan_canceled_c_dxe_count", text: "解約(C料金(DXE))", align: "right" },
        { value: "type_web_count", text: "Web登録数", align: "right" },
        { value: "type_paper_count", text: "紙登録数", align: "right" },
        { value: "total_count", text: "合計数", align: "right" },
        { value: "send_count", text: "データ送信数", align: "right" },
        { value: "jwnet_transfer_count", text: "JWNETデータ取込数", align: "right" },
      ],
      manifest_reports: [],
    };
  },
  computed: {
    ...mapState("AdminManifestReport", {
      pagination: "pagination",
    }),
    ...mapGetters("AdminManifestReport", ["hasResult"]),
  },
  created() {
    this.searchForms.startMonth = moment().format("YYYY-MM")
    this.searchForms.endMonth = moment().format("YYYY-MM")

    this.searchManifestReport();
  },
  methods: {
    ...mapActions("AdminManifestReport", {
      fetchManifestReports: "index",
    }),
    searchButtonClickEvent() {
      this.page = 1;
      this.searchManifestReport();
    },
    changePage() {
      this.searchManifestReport();
    },
    /**
     * CSV出力
     */
    exportButtonClickEvent() {
      let params = this.buildParam('csv')
      this.fetchManifestReports(params).then((content) => {
        this.downloadCsv(content, 'マニフェスト登録件数')
      })
    },
    /**
     * 対象年月(From)変更　コールバック
     */
    startMonthChangedCallback (value) {
      this.searchForms.startMonth = value;
    },
    /**
     * 対象年月(To)変更　コールバック
     */
    endMonthChangedCallback (value) {
      this.searchForms.endMonth = value;
    },
    /**
     * 通常の検索
     */
    searchManifestReport() {
      let params = this.buildParam("json", null);
      this.fetchManifestReports(params).then((content) => {
        this.manifest_reports = content.manifest_reports;
        this.total_count = content.pagination.total_count;
      });
    },
    /*パラメータ設定 */
    buildParam(format, id) {
      return {
        start_month: this.searchForms.startMonth,
        end_month: this.searchForms.endMonth,
        page: this.page,
        format: format,
      };
    },
  },
};
</script>

<style scoped>
</style>
